import { Injectable, Input } from '@angular/core';
import { Subject } from 'rxjs';
import { SampleStatusInfo } from './sample-status-info';
import { SampleCountData } from '../dashboard-info';
import { Settings } from '../../../../core';

export const powers = [
    { key: 'Q', value: Math.pow(10, 15) },
    { key: 'T', value: Math.pow(10, 12) },
    { key: 'B', value: Math.pow(10, 9) },
    { key: 'M', value: Math.pow(10, 6) },
    { key: 'K', value: 1000 }
];

@Injectable()
export class SampleStatusService {

    public clearSelectedFilters = new Subject<boolean>();

    public clearFilters$ = this.clearSelectedFilters.asObservable();

    constructor(private settings: Settings) {
    }

    public clearFilterSelection(mission: boolean) {
        this.clearSelectedFilters.next(mission);
    }

    public getSampleStatusInfo(sampleData: SampleCountData) {
        
        const sampleCountList: SampleStatusInfo[] = [
            {
                id: 'SamplesCancelled',
                label: 'Cancelled Samples',
                count: sampleData.samplesCancelled,
                formatedCount: this.transformCount(sampleData.samplesCancelled),
                imgName: 'samples-cancelled.svg',
                showCount: true,
                filterItem: {
                    id: 7, value: 'Cancelled Samples', key: 'SamplesCancelled',
                    viewName: 'cancelled-listview'
                },
                isHold: true,
                rowId: 1,
                imgSize: true,
                tooltip: this.settings.sampleTrackingCancelledSamplesHelpText
            },
            {
                id: 'SamplesOnHold',
                label: 'Samples in Visits with <br>Active eQueries',
                count: sampleData.samplesOnHold,
                formatedCount: this.transformCount(sampleData.samplesOnHold),
                imgName: 'samples-hold.svg',
                showCount: true,
                filterItem: {
                    id: 9, value: 'Visits with an Active eQuery',
                    timeFrameLabel: 'eQuery Open Date Timeframe',
                    key: 'SamplesOnHold',
                    helpText: 'Samples are grouped by Visit because eQueries apply to Visits, not particular samples.'
                },
                isHold: true,
                imgSize: true,
                rowId: 1,
                tooltip: this.settings.sampleTrackingAffectedByHoldSamplesHelpText,
                buttonLabel: 'View Visits'
            },
            {
                id: 'SamplesNotAvailable',
                label: 'Samples Not Available',
                count: 0,
                formatedCount: '',
                imgName: 'samples-not-available.svg',
                showCount: false,
                filterItem: {
                    id: 10, value: 'Samples Not Available', subValue: 'Required', enableReturnTypeFilter: true,
                    key: 'SamplesNotAvailable', isQuickFilterEnabled: true,
                    viewName: 'notavailable-listview',
                    disclaimerText: 'Sample Information entered through Sample Registration is subject to change upon receipt at Labcorp.',
                    isMissMatchIconAppear: true
                },
                isHold: true,
                imgSize: true,
                rowId: 1,
                tooltip: this.settings.sampleTrackingNotAvailableHelpText
            },
            {
                id: 'samplesReceived',
                label: 'Samples Received',
                count: sampleData.samplesReceived,
                formatedCount: this.transformCount(sampleData.samplesReceived),
                filterItem: { id: 3, value: 'Received Samples', viewName: 'received-listview', key: 'samplesReceived' },
                showCount: true,
                imgName: 'samples-received.svg',
                rowId: 2,
                imgSize: true,
                tooltip: this.settings.sampleTrackingReceivedSamplesHelpText
            },
            {
                id: 'samplesInInventory',
                label: 'Samples In Inventory',
                count: sampleData.samplesInInventory,
                formatedCount: this.transformCount(sampleData.samplesInInventory),
                imgName: 'storage.png',
                showCount: true,
                filterItem: {
                    id: 5, value: 'Samples In Inventory',
                    viewName: 'ininventory-listview', key: 'samplesInInventory'
                },
                rowId: 2,
                tooltip: this.settings.sampleTrackingInInventorySamplesHelpText
            },
            {
                id: 'samplesShipped',
                label: 'Samples Shipped from <br> Labcorp',
                count: sampleData.samplesShipped,
                formatedCount: this.transformCount(sampleData.samplesShipped),
                imgName: 'shipped_from_labcorp.svg',
                showCount: true,
                filterItem: {
                    id: 4, value: 'Samples Shipped from Labcorp',
                    key: 'samplesShipped', viewName: 'shipped-listview'
                },
                rowId: 2,
                tooltip: this.settings.sampleTrackingShippedFromLabcorpSamplesHelpText
            },
            {
                id: 'samplesNotReceived',
                label: 'Samples Pending Arrival',
                count: sampleData.samplesNotReceived,
                formatedCount: this.transformCount(sampleData.samplesNotReceived),
                imgName: 'not_received.svg',
                showCount: true,
                filterItem: {
                    id: 2, value: 'Samples Pending Arrival',
                    key: 'samplesNotReceived', viewName: 'unreceived-listview',
                    disclaimerText: 'Sample Information entered through Sample Registration is subject to change upon receipt at Labcorp.'
                },
                rowId: 2,
                imgSize: true,
                tooltip: this.settings.sampleTrackingPendingArrivalSamplesHelpText
            },
            {
                id: 'samplesCollected',
                label: 'Samples Collected',
                count: sampleData.samplesCollected,
                formatedCount: this.transformCount(sampleData.samplesCollected),
                imgName: 'samples_collected.svg',
                showCount: true,
                filterItem: { id: 9, value: 'Samples Collected', key: 'samplesCollected' },
                disableNavigation: true,
                imgSize: true,
                rowId: 2,
            },
            {
                id: 'ShippedToLabcorp',
                label: 'Samples Shipped from <br> Site to Labcorp',
                count: sampleData.samplesShippedFromSiteToLabcorp,
                formatedCount: this.transformCount(sampleData.samplesShippedFromSiteToLabcorp),
                imgName: 'shipped_from_site_to_labcorp.png',
                showCount: false,
                filterItem: {
                    id: 8, value: 'Samples Shipped from Site', subValue: 'Labcorp', enableShippedToFilter: true,
                    isQuickFilterEnabled: true,
                    key: 'ShippedToLabcorp',
                    viewName: 'shipped-from-site'
                },
                imgSize: true,
                rowId: 3,
                tooltip: this.settings.sampleTrackingShippedFromSiteToLabcorpSamplesHelpText
            },
            {
                id: 'ShippedToThirdParty',
                label: 'Samples Shipped from <br> Site to Third Party',
                count: sampleData.samplesShippedFromSiteToThirdParty,
                formatedCount: this.transformCount(sampleData.samplesShippedFromSiteToThirdParty),
                imgName: 'shipped_to_third_party.svg',
                showCount: false,
                filterItem: {
                    id: 8, value: 'Samples Shipped from Site', subValue: 'ThirdParty', enableShippedToFilter: true,
                    key: 'ShippedToThirdParty', isQuickFilterEnabled: true,
                    viewName: 'shipped-from-site'
                },
                rowId: 3,
                imgSize: true,
                tooltip: this.settings.sampleTrackingShippedFromSiteToThirdPartySamplesHelpText
            },
            {
                id: 'samplesDiscarded',
                label: 'Samples Discarded from <br> Specimen Management',
                count: sampleData.samplesDiscarded,
                formatedCount: this.transformCount(sampleData.samplesDiscarded),
                imgName: 'samples_discarded.svg',
                showCount: true,
                filterItem: {
                    id: 6, value: 'Samples Discarded from Specimen Management', key: 'samplesDiscarded',
                    viewName: 'smdiscarded-listview'
                },
                rowId: 3,
                imgSize: true,
                tooltip: this.settings.sampleTrackingDiscardedFromSpecimenManagementSamplesHelpText
            },
            {
                id: 'samplesRemoved',
                label: 'Samples Removed',
                count: sampleData.samplesRemoved,
                formatedCount: this.transformCount(sampleData.samplesRemoved),
                imgName: 'samples_removed.svg',
                showCount: true,
                filterItem: {
                    id: 6, value: 'Samples Removed from Specimen Management', key: 'samplesRemoved',
                    viewName: 'smremoved-listview'
                },
                rowId: 3,
                disableNavigation: true,
                imgSize: true,
                tooltip: this.settings.sampleTrackingRemovedFromSpecimenManagementSamplesHelpText
            },
            {
                id: 'allSamples',
                label: 'All Samples',
                count: 0,
                formatedCount: '',
                imgName: 'all_samples.svg',
                showCount: false,
                filterItem: { id: 1, value: 'All Samples', key: 'allSamples', viewName: 'allsamples-listview' },
                rowId: 3,
                tooltip: this.settings.sampleTrackingAllSamplesHelpText
            }
        ];
        return sampleCountList;
    }

    public transformCount(inputNum: number): string {
        if (isNaN(inputNum) || inputNum === null || inputNum === 0 || inputNum < 1000) {
            return '';
        }

        let absoluteVal = Math.abs(inputNum);
        let key = '';

        for (let power of powers) {
            let reducedValue = Math.round((absoluteVal / power.value) * 10) / 10;
            if (reducedValue >= 1) {
                absoluteVal = reducedValue;
                key = power.key;
                break;
            }
        }
        return absoluteVal + key;
    }

    public getAllSamplesStatusInfo(): SampleStatusInfo {
        return {
            id: 'allSamples',
            label: 'All Samples',
            count: 0,
            formatedCount: '',
            imgName: 'all_samples.svg',
            showCount: false,
            filterItem: { id: 1, value: 'All Samples', key: 'allSamples', viewName: 'allsamples-listview' },
            rowId: 3,
            tooltip: this.settings.sampleTrackingAllSamplesHelpText
        };
    }
}